import { create } from 'zustand';
import {
  configDb,
  type CompareConfigEntry,
  type ComparePeriod,
  type CompareGranularity,
} from '@/persistence/config-db';
import { getQueryWorker } from '@/engine/worker-client';

/**
 * Active compare-mode configuration per dataset (D-020). One config
 * per dataset; switching datasets reloads it. The store is keyed by
 * `datasetId` so switching datasets without going through the store
 * (e.g. via a route change) reads the right config on next access.
 */

type State = {
  /** Currently-loaded config, or null if the user hasn't set one yet. */
  config: CompareConfigEntry | null;
  /** Which dataset the loaded config corresponds to. */
  datasetId: string | null;

  loadForDataset: (id: string) => Promise<void>;
  /** Persist a fully-formed config to Dexie + memory. */
  save: (params: {
    datasetId: string;
    dateColumn: string;
    granularity: CompareGranularity;
    periodA: ComparePeriod;
    periodB: ComparePeriod;
  }) => Promise<void>;
  /** Drop the persisted config (returns compare mode to its empty state). */
  clear: () => Promise<void>;
};

export const useCompareConfigStore = create<State>((set, get) => ({
  config: null,
  datasetId: null,

  loadForDataset: async (id) => {
    set({ datasetId: id, config: null });
    try {
      const entry = await configDb.compareConfigs.get(id);
      if (get().datasetId !== id) return;
      set({ config: entry ?? null });
    } catch (err) {
      // Non-fatal — leave config null. Log so a Dexie schema mismatch
      // or quota error shows up in production debugging.
      console.warn('compareConfig load failed', err);
    }
  },

  save: async ({ datasetId, dateColumn, granularity, periodA, periodB }) => {
    const entry: CompareConfigEntry = {
      datasetId,
      dateColumn,
      granularity,
      periodA,
      periodB,
      updatedAt: Date.now(),
    };
    try {
      await configDb.compareConfigs.put(entry);
    } catch (err) {
      // Non-fatal — in-memory still updates. Log so an over-quota
      // write doesn't disappear silently.
      console.warn('compareConfig save failed', err);
    }
    set({ config: entry, datasetId });
  },

  clear: async () => {
    const id = get().datasetId;
    if (!id) {
      set({ config: null });
      return;
    }
    try {
      await configDb.compareConfigs.delete(id);
    } catch (err) {
      // Non-fatal — surface in console for debugging.
      console.warn('compareConfig delete failed', err);
    }
    // Drop only the compare-prefixed cache entries so a subsequent
    // Karşılaştır click can't serve stale rows from the previous
    // period selection. Normal-mode results stay warm — toggling
    // compare off doesn't punish the user's main dashboard with a
    // cold cache (R-20).
    try {
      await getQueryWorker().invalidateCompareCache(id);
    } catch (err) {
      // Worker may not be running yet — fine, nothing to invalidate.
      console.warn('invalidateCompareCache failed', err);
    }
    set({ config: null });
  },
}));
